import React, { useState, useEffect, useCallback } from 'react';
import { i9API } from '../../services/api';

const STATUS_OPTIONS = [
  { value: '',                  label: 'All Statuses' },
  { value: 'link_sent',         label: 'Link Sent' },
  { value: 'employee_complete', label: 'Employee Complete' },
  { value: 'hr_complete',       label: 'HR Complete' },
  { value: 'approved',          label: 'Approved' },
];

const STATUS_COLORS = {
  link_sent:          'bg-yellow-100 text-yellow-800',
  employee_complete:  'bg-blue-100 text-blue-800',
  hr_complete:        'bg-purple-100 text-purple-800',
  approved:           'bg-green-100 text-green-800',
};

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || 'bg-slate-100 text-slate-700';
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${color}`}>
      {(status || 'unknown').replace(/_/g, ' ')}
    </span>
  );
}

function Check({ done }) {
  return done
    ? <svg className="w-5 h-5 text-green-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
    : <span className="text-slate-300 text-lg block text-center">—</span>;
}

// Parse employee_section_data — comes as JSON string or object
function parseSection(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
}

function SectionDataModal({ record, onClose }) {
  const data = parseSection(record.employee_section_data);
  const fields = [
    ['Full Name',            data?.full_name],
    ['Date of Birth',        data?.date_of_birth],
    ['SSN Last 4',           data?.ssn_last4 ? `****${data.ssn_last4}` : null],
    ['Address',              data?.address],
    ['Citizenship Status',   data?.citizenship_status?.replace(/_/g, ' ')],
    ['Document Title',       data?.document_title],
    ['Issuing Authority',    data?.document_issuing_authority],
    ['Document Number',      data?.document_number],
    ['Expiry Date',          data?.document_expiry || 'N/A'],
  ];
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="font-semibold text-slate-800">I-9 Section 1 — Employee Submission</h3>
            <p className="text-xs text-slate-500 mt-0.5">{record.candidate_name || record.employee_name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl font-bold px-1">✕</button>
        </div>
        <div className="px-6 py-4 space-y-2 max-h-96 overflow-y-auto">
          {!data ? (
            <p className="text-slate-500 text-sm">No submission data available.</p>
          ) : fields.map(([label, value]) => (
            <div key={label} className="flex gap-3 text-sm py-1.5 border-b border-slate-50">
              <span className="text-slate-500 font-medium w-44 shrink-0">{label}</span>
              <span className="text-slate-800 capitalize">{value || <span className="text-slate-400">—</span>}</span>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 text-right">
          <button onClick={onClose} className="text-sm text-slate-600 hover:text-slate-900 font-medium">Close</button>
        </div>
      </div>
    </div>
  );
}

export default function I9List() {
  const [records, setRecords]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [msg, setMsg]             = useState('');
  const [statusFilter, setStatus] = useState('');
  const [busy, setBusy]           = useState(null);
  const [viewing, setViewing]     = useState(null); // record being viewed

  const fetchRecords = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await i9API.list(params);
      setRecords(res.data?.data?.records || res.data?.records || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load I-9 records.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  async function hrComplete(record) {
    setBusy(record.id); setMsg(''); setError('');
    try {
      await i9API.hrComplete({ i9_id: record.id });
      setMsg(`HR section completed for ${record.candidate_name || record.employee_name}.`);
      fetchRecords();
    } catch (err) {
      setError(err.response?.data?.message || 'Action failed.');
    } finally { setBusy(null); }
  }

  async function adminApprove(record) {
    setBusy(record.id); setMsg(''); setError('');
    try {
      await i9API.adminApprove({ i9_id: record.id });
      setMsg(`I-9 approved for ${record.candidate_name || record.employee_name}.`);
      fetchRecords();
    } catch (err) {
      setError(err.response?.data?.message || 'Action failed.');
    } finally { setBusy(null); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">I-9 Workflow</h1>
          <p className="text-slate-500 text-sm mt-1">Track Form I-9 completion for all employees and candidates.</p>
        </div>
      </div>

      {msg   && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm">{msg}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>}

      <div className="flex gap-3">
        <select value={statusFilter} onChange={(e) => setStatus(e.target.value)}
          className="border border-slate-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button onClick={fetchRecords}
          className="border border-slate-300 rounded-lg px-4 py-2 text-sm hover:bg-slate-50 transition-colors">
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : records.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <p className="font-medium">No I-9 records found</p>
            <p className="text-sm mt-1">Send I-9 links from candidate profiles to start the workflow.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Status</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600">Employee Done</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600">HR Done</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600">Admin Approved</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Sent</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {records.map((r) => {
                  const empDone   = !!r.employee_section_completed_at;
                  const hrDone    = !!r.hr_section_completed_at;
                  const adminDone = !!r.admin_approved_at;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {r.candidate_name || r.employee_name || `#${r.id}`}
                      </td>
                      <td className="px-4 py-3 text-slate-500 capitalize">{r.entity_type || 'candidate'}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3"><Check done={empDone} /></td>
                      <td className="px-4 py-3"><Check done={hrDone} /></td>
                      <td className="px-4 py-3"><Check done={adminDone} /></td>
                      <td className="px-4 py-3 text-slate-500">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {empDone && (
                            <button onClick={() => setViewing(r)}
                              className="text-blue-600 hover:text-blue-800 text-xs font-medium">
                              View Form
                            </button>
                          )}
                          {empDone && !hrDone && (
                            <button onClick={() => hrComplete(r)} disabled={busy === r.id}
                              className="text-purple-600 hover:text-purple-800 text-xs font-medium disabled:opacity-50">
                              {busy === r.id ? '...' : 'HR Complete'}
                            </button>
                          )}
                          {hrDone && !adminDone && (
                            <button onClick={() => adminApprove(r)} disabled={busy === r.id}
                              className="text-green-600 hover:text-green-800 text-xs font-medium disabled:opacity-50">
                              {busy === r.id ? '...' : 'Approve'}
                            </button>
                          )}
                          {adminDone && (
                            <span className="text-green-600 text-xs font-medium">✓ Complete</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewing && <SectionDataModal record={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
